import { BeconCommonTypes } from "@becon-team/becon-fe-lib/my-types";
import { z } from "zod";

import {
  SkinMicroCandidateDtoSchema,
  SkinMacroCandidateDtoSchema,
  SkinInquiryDtoSchema,
  SkinAnalysisResultDtoSchema,
  SkinAnalysisType,
  SkinHistoryStatisticsDtoSchema,
  GetSkinHistoryStatisticsRequestDtoSchema,
  GetSkinAnalysisSetRequestDtoSchema,
  SkinAverageScoreByMicroMeasureItemSchema,
  SkinAverageScoreByMicroPositionSchema,
  SkinMicroTrendSchema,
  SkinAnalysisResultV3DtoSchema,
} from "./skin.dto";

/** POST: 피부 분석 생성 */
export const PostSkinAnalysisSetResponseSchema = z.object({
  id: z.number(),
  user_id: z.string(),
  status: z.nativeEnum(BeconCommonTypes.SetStatusEnum),
  reg_date: z.string(),
});
export type PostSkinAnalysisSetResponse = z.infer<typeof PostSkinAnalysisSetResponseSchema>;

export interface PostSkinAnalysisSet {
  (arg: { userId: string }): Promise<PostSkinAnalysisSetResponse>;
}

/** GET: 피부 분석 상태 조회 (query x) */
export const GetMacroSkinAnalysisStatusResponseSchema = z.object({
  skin_analysis_set_id: z.number(),
  status: z.nativeEnum(BeconCommonTypes.SetStatusEnum),
});

export type GetMacroSkinAnalysisStatusResponse = z.infer<typeof GetMacroSkinAnalysisStatusResponseSchema>;

export interface GetMacroSkinAnalysisStatus {
  (arg: {
    userId: string;
    skinAnalysisSetId: number;
    type: SkinAnalysisType;
  }): Promise<GetMacroSkinAnalysisStatusResponse>;
}

/** GET: 피부 분석 상태 조회 (query x) */
export const GetMicroSkinAnalysisStatusResponseSchema = z.object({
  skinAnalysisSetId: z.number(),
  microSubsetStatus: z.nativeEnum(BeconCommonTypes.SetStatusEnum),
});

export type GetMicroSkinAnalysisStatusResponse = z.infer<typeof GetMicroSkinAnalysisStatusResponseSchema>;

export interface GetMicroSkinAnalysisStatus {
  (arg: { userId: string; skinAnalysisSetId: number }): Promise<GetMicroSkinAnalysisStatusResponse>;
}

/** POST: 피부 분석 진행 */
export interface PostSkinAnalysisAnalyze {
  (arg: { userId: string; skinAnalysisSetId: number }): Promise<unknown>;
}

/** GET: 피부 분석 결과 조회 */
export const GetSkinAnalysisResultResponseSchema = SkinAnalysisResultDtoSchema;
export type GetSkinAnalysisResultResponse = z.infer<typeof GetSkinAnalysisResultResponseSchema>;

export interface GetSkinAnalysisResult {
  (arg: { userId: string; skinAnalysisSetId: number; type: SkinAnalysisType }): Promise<GetSkinAnalysisResultResponse>;
}

/** PUT: 피부 분석 복사 */
export const PutSkinAnalysisSetDuplicateResponseSchema = z.object({
  id: z.number(),
  userId: z.string(),
  status: z.nativeEnum(BeconCommonTypes.SetStatusEnum),
  macroSubsetStatus: z.nativeEnum(BeconCommonTypes.SetStatusEnum),
  microSubsetStatus: z.nativeEnum(BeconCommonTypes.SetStatusEnum),
  regDate: z.string(),
});
export type PutSkinAnalysisSetDuplicateResponse = z.infer<typeof PutSkinAnalysisSetDuplicateResponseSchema>;

export interface PutSkinAnalysisSetDuplicate {
  (arg: { userId: string; skinAnalysisSetId: number }): Promise<PutSkinAnalysisSetDuplicateResponse>;
}

/** DELETE: 피부 후보 삭제 (매크로 & 마이크로) */
export interface DeleteSkinCandidate {
  (arg: { userId: string; skinAnalysisSetId: number; candidateId: number }): Promise<unknown>;
}

/**GET: 피부 문진 조회 */
export const GetSkinInquiryResponseSchema = z.object({
  user_id: z.string(),
  skin_inquiry: SkinInquiryDtoSchema.nullable(),
});
export type GetSkinInquiryResponse = z.infer<typeof GetSkinInquiryResponseSchema>;

export interface GetSkinInquiry {
  (arg: { userId: string }): Promise<GetSkinInquiryResponse>;
}

/** PUT: 피부 문진 수정 */
export const PutSkinInquiryRequestSchema = SkinInquiryDtoSchema;
export type PutSkinInquiryRequest = z.infer<typeof PutSkinInquiryRequestSchema>;

export interface PutSkinInquiry {
  (arg: { userId: string } & PutSkinInquiryRequest): Promise<unknown>;
}

/** GET: 피부 히스토리 통계 조회 */
export const GetSkinHistoryStatisticsResponseSchema = SkinHistoryStatisticsDtoSchema;
export type GetSkinHistoryStatisticsResponse = z.infer<typeof GetSkinHistoryStatisticsResponseSchema>;
export type GetSkinHistoryStatisticsRequest = z.infer<typeof GetSkinHistoryStatisticsRequestDtoSchema>;

export interface GetSkinHistoryStatistics {
  (arg: { userId: string } & GetSkinHistoryStatisticsRequest): Promise<GetSkinHistoryStatisticsResponse>;
}

export const SkinAnalysisSetDataSchema = z.array(
  z.object({
    id: z.number(),
    user_id: z.string(),
    status: z.nativeEnum(BeconCommonTypes.SetStatusEnum),
    macro_subset_status: z.nativeEnum(BeconCommonTypes.SetStatusEnum),
    micro_subset_status: z.nativeEnum(BeconCommonTypes.SetStatusEnum),
    macro_subset: z.array(SkinMacroCandidateDtoSchema),
    micro_subset: z.array(SkinMicroCandidateDtoSchema),
    reg_date: z.string(),
    analysis_date: z.string().nullable(),
  }),
);

export type SkinAnalysisSetDataDto = z.infer<typeof SkinAnalysisSetDataSchema>;

/** GET: 피부 분석 목록 조회  */
export const GetSkinAnalysisSetResponseSchema = z.object({
  data: SkinAnalysisSetDataSchema,
  total: z.number(),
  skip: z.number().nullable(),
  take: z.number().nullable(),
});
export type GetSkinAnalysisSetResponse = z.infer<typeof GetSkinAnalysisSetResponseSchema>;

export type GetSkinAnalysisSetRequest = z.infer<typeof GetSkinAnalysisSetRequestDtoSchema>;

export interface GetSkinAnalysisSet {
  (arg: { userId: string } & GetSkinAnalysisSetRequest): Promise<GetSkinAnalysisSetResponse>;
}

export const GetMicroSkinAnalysisTrendResponseSchema = z.object({
  trend: z.array(SkinMicroTrendSchema),
  average_score_by_position: z.array(SkinAverageScoreByMicroPositionSchema),
  average_score_by_measure_item: z.array(SkinAverageScoreByMicroMeasureItemSchema),
});

export type GetMicroSkinAnalysisTrendResponse = z.infer<typeof GetMicroSkinAnalysisTrendResponseSchema>;

export interface GetMicroSkinAnalysisTrend {
  (arg: { userId: string; timeOffset: string }): Promise<GetMicroSkinAnalysisTrendResponse>;
}

/** GET: 피부 평균 점수 조회 */
export const GetSkinAverageScoreResponseSchema = z.object({
  totalAverageScore: z.number(),
});

export type GetSkinAverageScoreResponse = z.infer<typeof GetSkinAverageScoreResponseSchema>;

export interface GetSkinAverageScore {
  (arg: { userId: string; type: SkinAnalysisType }): Promise<GetSkinAverageScoreResponse>;
}

/** GET: 피부 분석 결과 조회 B2B */
export const GetSkinAnalysisResultB2BResponseSchema = SkinAnalysisResultV3DtoSchema;
export type GetSkinAnalysisResultB2BResponse = z.infer<typeof GetSkinAnalysisResultB2BResponseSchema>;

export interface GetSkinAnalysisResultB2B {
  (arg: { userId: string; skinAnalysisSetId: number }): Promise<GetSkinAnalysisResultB2BResponse>;
}
